import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../contexts/AuthContext';
import { useGlobalSettings } from '../hooks/useGlobalSettings';

const forgotSchema = z.object({
  email: z.string().email('E-mail inválido'),
});

type ForgotFormData = z.infer<typeof forgotSchema>;

export function ForgotPasswordPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [apiMessage, setApiMessage] = useState('');
  const { forgotPassword } = useAuth();
  const { settings } = useGlobalSettings();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotFormData>({
    resolver: zodResolver(forgotSchema),
  });

  const onSubmit = async (data: ForgotFormData) => {
    setIsSubmitting(true);
    try {
      const result = await forgotPassword(data.email);
      if (result.success) {
        setEmailSent(true);
        setApiMessage(result.message);
      } else {
        setApiMessage(result.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] flex items-center justify-center p-4 font-oswald">
      <div className="bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 shadow-2xl w-full max-w-md overflow-hidden">
        <div className="p-12">
          <div className="mb-10 text-center">
            <div className="w-20 h-20 bg-white/10 rounded-[1.5rem] mx-auto mb-6 flex items-center justify-center backdrop-blur-xl border border-white/20 shadow-2xl">
              {settings?.logoUrl ? (
                <img src={settings.logoUrl} alt="Logo" className="w-14 h-14 object-contain" />
              ) : (
                <img src="/favicon.png" alt="Logo" className="w-14 h-14 object-contain" />
              )}
            </div>
            <h2 className="text-3xl font-bold text-white mb-2 uppercase tracking-tight">
              {emailSent ? 'E-mail enviado' : 'Redefinir Senha'}
            </h2>
            <p className="text-gray-400 text-xs uppercase tracking-[0.2em] font-medium">
              {emailSent
                ? 'Verifique sua caixa de entrada'
                : 'Digite seu e-mail para receber o link'}
            </p>
          </div>

          {emailSent ? (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full mx-auto flex items-center justify-center">
                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">{apiMessage}</p>
              <Link
                to="/login"
                className="inline-block w-full py-4 bg-blue-600 text-white rounded-2xl font-bold text-xs uppercase tracking-[0.2em] hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all hover:scale-[1.02] active:scale-[0.98] border border-blue-400/50 text-center"
              >
                Voltar para o Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="email" className="text-[10px] font-bold text-blue-400 uppercase tracking-[0.2em] ml-2">
                  E-mail
                </label>
                <div className="relative group">
                  <input
                    {...register('email')}
                    type="email"
                    id="email"
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                    placeholder="seu@email.com"
                    disabled={isSubmitting}
                  />
                </div>
                {errors.email && (
                  <p className="text-red-400 text-[10px] uppercase font-bold ml-2">{errors.email.message}</p>
                )}
                {apiMessage && !errors.email && (
                  <p className="text-yellow-400 text-[10px] uppercase font-bold ml-2">{apiMessage}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-blue-600 text-white rounded-2xl font-bold font-oswald text-xs uppercase tracking-[0.2em] hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all hover:scale-[1.02] active:scale-[0.98] border border-blue-400/50 relative overflow-hidden group/btn disabled:opacity-50"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/20 border-t-white"></div>
                    Enviando...
                  </div>
                ) : (
                  'Enviar Link de Redefinição'
                )}
              </button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-blue-400/70 hover:text-blue-300 text-[10px] uppercase tracking-[0.15em] font-bold transition-colors"
                >
                  Voltar para o Login
                </Link>
              </div>
            </form>
          )}

          <div className="mt-8 text-center">
            <p className="text-gray-500 text-[9px] uppercase tracking-[0.3em] font-bold">
              © {new Date().getFullYear()} {settings?.companyName || 'BChat'} • Todos os direitos reservados
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
